<template>
  <div class="progress-container">
    <div class="progress-header">
      <div class="progress-info">
        <span class="progress-percentage">{{ Math.round(progress) }}%</span>
        <span class="progress-counts">
          {{ completedCount + failedCount }} / {{ totalCount }}
        </span>
      </div>
      <div class="status-badge" :class="statusClass">
        {{ statusLabel }}
      </div>
    </div>

    <div class="progress-bar-container">
      <div class="progress-bar">
        <div
          class="progress-bar-completed"
          :style="{ width: completedPercentage + '%' }"
        ></div>
        <div
          class="progress-bar-failed"
          :style="{ width: failedPercentage + '%', left: completedPercentage + '%' }"
        ></div>
      </div>
    </div>

    <div class="progress-legend">
      <div class="legend-item">
        <span class="legend-dot legend-dot--completed"></span>
        <span>Completate: {{ completedCount }}</span>
      </div>
      <div class="legend-item">
        <span class="legend-dot legend-dot--failed"></span>
        <span>Fallite: {{ failedCount }}</span>
      </div>
      <div class="legend-item">
        <span class="legend-dot legend-dot--pending"></span>
        <span>In attesa: {{ pendingCount }}</span>
      </div>
    </div>

    <div class="progress-actions" v-if="showActions">
      <button
        v-if="canPause"
        @click="$emit('pause')"
        class="action-button action-button--pause"
        :disabled="loading"
      >
        <svg viewBox="0 0 24 24" fill="currentColor" class="action-icon">
          <rect x="6" y="4" width="4" height="16" />
          <rect x="14" y="4" width="4" height="16" />
        </svg>
        Pausa
      </button>

      <button
        v-if="canResume"
        @click="$emit('resume')"
        class="action-button action-button--resume"
        :disabled="loading"
      >
        <svg viewBox="0 0 24 24" fill="currentColor" class="action-icon">
          <polygon points="5,3 19,12 5,21" />
        </svg>
        Riprendi
      </button>

      <button
        v-if="canCancel"
        @click="$emit('cancel')"
        class="action-button action-button--cancel"
        :disabled="loading"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" class="action-icon">
          <line x1="18" y1="6" x2="6" y2="18" stroke-width="2" stroke-linecap="round" />
          <line x1="6" y1="6" x2="18" y2="18" stroke-width="2" stroke-linecap="round" />
        </svg>
        Annulla
      </button>

      <button
        v-if="canDownload"
        @click="$emit('download')"
        class="action-button action-button--download"
        :disabled="loading"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" class="action-icon">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <polyline points="7,10 12,15 17,10" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <line x1="12" y1="15" x2="12" y2="3" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        Scarica
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BulkJobProgress',
  props: {
    status: {
      type: String,
      required: true,
      validator: (value) =>
        ['pending', 'processing', 'paused', 'completed', 'cancelled', 'failed'].includes(value),
    },
    totalCount: {
      type: Number,
      default: 0,
    },
    completedCount: {
      type: Number,
      default: 0,
    },
    failedCount: {
      type: Number,
      default: 0,
    },
    showActions: {
      type: Boolean,
      default: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['pause', 'resume', 'cancel', 'download'],
  computed: {
    progress() {
      if (this.totalCount === 0) return 0;
      return ((this.completedCount + this.failedCount) / this.totalCount) * 100;
    },
    completedPercentage() {
      if (this.totalCount === 0) return 0;
      return (this.completedCount / this.totalCount) * 100;
    },
    failedPercentage() {
      if (this.totalCount === 0) return 0;
      return (this.failedCount / this.totalCount) * 100;
    },
    pendingCount() {
      return Math.max(0, this.totalCount - this.completedCount - this.failedCount);
    },
    statusClass() {
      return `status-badge--${this.status}`;
    },
    statusLabel() {
      const labels = {
        pending: 'In attesa',
        processing: 'In elaborazione',
        paused: 'In pausa',
        completed: 'Completato',
        cancelled: 'Annullato',
        failed: 'Fallito',
      };
      return labels[this.status] || this.status;
    },
    canPause() {
      return ['pending', 'processing'].includes(this.status);
    },
    canResume() {
      return this.status === 'paused';
    },
    canCancel() {
      return ['pending', 'processing', 'paused'].includes(this.status);
    },
    canDownload() {
      return ['completed', 'cancelled', 'failed'].includes(this.status);
    },
  },
};
</script>

<style scoped>
.progress-container {
  padding: 1rem;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}

.progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.progress-info {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
}

.progress-percentage {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1a1a1a;
}

.progress-counts {
  font-size: 0.875rem;
  color: #6b7280;
}

.status-badge {
  padding: 0.25rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  border-radius: 4px;
}

.status-badge--pending {
  background: #f3f4f6;
  color: #6b7280;
}

.status-badge--processing {
  background: #dbeafe;
  color: #1d4ed8;
}

.status-badge--paused {
  background: #fef3c7;
  color: #b45309;
}

.status-badge--completed {
  background: #d1fae5;
  color: #059669;
}

.status-badge--cancelled {
  background: #f3f4f6;
  color: #6b7280;
}

.status-badge--failed {
  background: #fee2e2;
  color: #dc2626;
}

.progress-bar-container {
  margin-bottom: 0.75rem;
}

.progress-bar {
  position: relative;
  height: 8px;
  background: #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
}

.progress-bar-completed {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background: #10b981;
  transition: width 0.3s ease;
}

.progress-bar-failed {
  position: absolute;
  top: 0;
  height: 100%;
  background: #ef4444;
  transition: width 0.3s ease, left 0.3s ease;
}

.progress-legend {
  display: flex;
  gap: 1rem;
  font-size: 0.75rem;
  color: #6b7280;
  margin-bottom: 1rem;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.legend-dot--completed {
  background: #10b981;
}

.legend-dot--failed {
  background: #ef4444;
}

.legend-dot--pending {
  background: #e5e7eb;
}

.progress-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.action-button {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  border: 1px solid;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.action-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.action-icon {
  width: 16px;
  height: 16px;
}

.action-button--pause {
  background: white;
  border-color: #f59e0b;
  color: #b45309;
}

.action-button--pause:hover:not(:disabled) {
  background: #fef3c7;
}

.action-button--resume {
  background: white;
  border-color: #10b981;
  color: #059669;
}

.action-button--resume:hover:not(:disabled) {
  background: #d1fae5;
}

.action-button--cancel {
  background: white;
  border-color: #ef4444;
  color: #dc2626;
}

.action-button--cancel:hover:not(:disabled) {
  background: #fee2e2;
}

.action-button--download {
  background: #2e4eed;
  border-color: #2e4eed;
  color: white;
}

.action-button--download:hover:not(:disabled) {
  background: #1e3ed8;
}

@media (max-width: 480px) {
  .progress-legend {
    flex-direction: column;
    gap: 0.25rem;
  }

  .progress-actions {
    flex-direction: column;
  }

  .action-button {
    width: 100%;
    justify-content: center;
  }
}
</style>
